import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", text: "", category: "none", photo_url: "", role: "" };

export default function ContentAdmin() {
  const [videoUrl, setVideoUrl] = useState(null);
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [editing, setEditing] = useState(null);
  const [savingVideo, setSavingVideo] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then(({ data }) => setVideoUrl(data.video_url || ""))
      .catch((e) => toast.error(formatApiError(e, "Erro ao carregar configurações.")));
    load();
  }, []);

  async function load() {
    try {
      const { data } = await api.get("/testimonials");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao carregar depoimentos."));
      setItems([]);
    }
  }

  async function saveVideo() {
    setSavingVideo(true);
    try {
      const { data: s } = await api.get("/admin/settings");
      s.video_url = (videoUrl || "").trim();
      await api.put("/admin/settings", s);
      toast.success(s.video_url ? "Vídeo salvo! A seção já aparece no site." : "Vídeo removido. A seção fica oculta no site.");
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao salvar o vídeo."));
    } finally {
      setSavingVideo(false);
    }
  }

  async function saveItem(e) {
    e.preventDefault();
    if (savingItem) return;
    setSavingItem(true);
    try {
      const payload = {
        name: form.name,
        text: form.text,
        category: form.category === "none" ? null : form.category,
        role: form.role || null,
        photo_url: form.photo_url || null,
      };
      if (editing) {
        await api.patch(`/admin/testimonials/${editing}`, payload);
        toast.success("Depoimento atualizado!");
      } else {
        await api.post("/admin/testimonials", payload);
        toast.success("Depoimento publicado no site!");
      }
      setForm({ ...EMPTY });
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Erro ao salvar depoimento."));
    } finally {
      setSavingItem(false);
    }
  }

  async function removeItem(id, name) {
    if (!window.confirm(`Excluir o depoimento de ${name}?`)) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Depoimento excluído. O site volta a mostrar os placeholders.");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao excluir."));
    }
  }

  function startEdit(t) {
    setEditing(t.id);
    setForm({ name: t.name, text: t.text, category: t.category || "none", photo_url: t.photo_url || "", role: t.role || "" });
  }

  if (videoUrl === null || items === null) {
    return (
      <div className="flex justify-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-content-view">
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-bold text-slate-900">Vídeo da Isabela</h3>
        <p className="mt-1 text-sm text-slate-500">
          Cole o link do YouTube ou Vimeo. Enquanto estiver vazio, a seção de vídeo fica oculta no site.
          (Envio de arquivo direto: em breve.)
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Input
            data-testid="settings-video-input"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=... ou https://vimeo.com/..."
          />
          <button
            data-testid="settings-video-save"
            onClick={saveVideo}
            disabled={savingVideo}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {savingVideo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar vídeo
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-bold text-slate-900">Depoimentos</h3>
        <p className="mt-1 text-sm text-slate-500">
          Publique depoimentos reais (com autorização do cliente). Sem cadastro, o site mostra os
          placeholders claramente marcados — nada é inventado.
        </p>

        <form onSubmit={saveItem} className="mt-5 space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="testimonial-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Nome do cliente</label>
              <Input
                data-testid="testimonial-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex.: Maria S."
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Tipo</label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="testimonial-category-select" className="w-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não especificar</SelectItem>
                  <SelectItem value="imovel">Imóvel</SelectItem>
                  <SelectItem value="veiculo">Veículo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Cidade/UF</label>
              <Input
                data-testid="testimonial-role-input"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                placeholder="Ex.: Salvador/BA (opcional)"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Texto do depoimento</label>
            <Textarea
              data-testid="testimonial-text-input"
              required
              minLength={5}
              rows={3}
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              placeholder="O que o cliente disse sobre planejar com você..."
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Foto (link, opcional)</label>
            <Input
              data-testid="testimonial-photo-input"
              value={form.photo_url}
              onChange={(e) => setForm((f) => ({ ...f, photo_url: e.target.value }))}
              placeholder="https://... (deixe vazio para usar as iniciais)"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              data-testid="testimonial-save-button"
              disabled={savingItem}
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
            >
              {savingItem ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editing ? "Salvar alterações" : "Publicar depoimento"}
            </button>
            {editing && (
              <button
                type="button"
                data-testid="testimonial-cancel-edit"
                onClick={() => { setEditing(null); setForm({ ...EMPTY }); }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-white"
              >
                <X className="h-4 w-4" /> Cancelar
              </button>
            )}
          </div>
        </form>

        <div className="mt-5 space-y-3" data-testid="testimonial-list">
          {items.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">
              Nenhum depoimento publicado ainda — o site exibe os placeholders.
            </p>
          ) : (
            items.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 p-4" data-testid={`testimonial-item-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-sm font-bold text-slate-800">{t.name}</p>
                    {t.category && (
                      <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-bold text-brand-600">
                        {t.category === "imovel" ? "Imóvel" : "Veículo"}
                      </span>
                    )}
                    {t.role && <span className="text-[11px] font-semibold text-slate-400">{t.role}</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{t.text}</p>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <button
                    data-testid={`testimonial-edit-${t.id}`}
                    onClick={() => startEdit(t)}
                    aria-label={`Editar depoimento de ${t.name}`}
                    className="rounded-full p-2 text-slate-500 transition-colors hover:bg-brand-50 hover:text-brand-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`testimonial-delete-${t.id}`}
                    onClick={() => removeItem(t.id, t.name)}
                    aria-label={`Excluir depoimento de ${t.name}`}
                    className="rounded-full p-2 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
